import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Spline, X } from 'lucide-react';
import type { Drill, PlanItem } from '@hoopify/shared';
import { api } from '../../lib/api';
import { useLibrary } from '../../lib/library';
import { searchDrills, type DrillMatch } from '../../lib/drillSearch';
import * as P from '../../lib/plan';
import { Button } from '../ui';

const clampMinutes = (v: string) => Math.min(180, Math.max(1, Math.round(Number(v)) || 1));

/** Your drills and drills shared with you. Same cache as the library panel. */
function useMyDrills(scope: 'mine' | 'shared') {
  return useQuery({ queryKey: ['drills', scope, '', null], queryFn: () => api.get<Drill[]>(`/drills?scope=${scope}`), staleTime: 60_000 });
}

const ORIGIN_LABEL = { mine: 'Mine', shared: 'Shared', hoopify: 'Hoopify' } as const;

/**
 * Type a drill's name. Matching drills from your library, shared drills and the Hoopify library
 * show underneath: ↑/↓ to choose, Enter to add, Esc to close the list (Esc again to stop adding).
 * The last row adds what you typed as a quick drill.
 */
export function QuickAdd({ label, onAdd, onCancel, onLibrary }: { label: string; onAdd: (item: PlanItem) => void; onCancel: () => void; onLibrary: () => void }) {
  const [title, setTitle] = useState('');
  const [minutes, setMinutes] = useState('10');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const listId = useId();
  useEffect(() => ref.current?.focus(), []);

  const mine = useMyDrills('mine');
  const shared = useMyDrills('shared');
  const hoopify = useLibrary();
  const q = title.trim();
  const matches = useMemo(
    () => (q ? searchDrills(q, { mine: mine.data, shared: shared.data, hoopify: hoopify.data }) : []),
    [q, mine.data, shared.data, hoopify.data],
  );
  const loading = mine.isLoading || shared.isLoading || hoopify.isLoading;
  const showList = open && !!q;
  // Rows: the matches, then "add as a quick drill".
  const rowCount = matches.length + 1;
  const current = Math.min(active, rowCount - 1);
  const optionId = (i: number) => `${listId}-${i}`;

  const reset = () => {
    setTitle('');
    setActive(0);
    setOpen(false);
    ref.current?.focus();
  };
  const addQuick = () => {
    if (!q) return;
    onAdd(P.quickItem(q.slice(0, 120), clampMinutes(minutes)));
    reset();
  };
  const pick = (m: DrillMatch) => {
    onAdd(m.make());
    reset();
  };
  const choose = (i: number) => (i < matches.length ? pick(matches[i]!) : addQuick());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    addQuick();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (showList) setOpen(false);
      else onCancel();
      return;
    }
    if (!q) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showList) {
        setOpen(true);
        setActive(e.key === 'ArrowDown' ? 0 : rowCount - 1);
        return;
      }
      setActive((current + (e.key === 'ArrowDown' ? 1 : rowCount - 1)) % rowCount);
    } else if (e.key === 'Enter' && showList) {
      e.preventDefault();
      choose(current);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-2 rounded-lg border border-action/40 bg-action-soft/40 p-3">
      <p className="mb-2 text-[13px] font-medium">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={ref}
          className="field-input min-w-[180px] flex-1"
          placeholder="Search or type a drill"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onFocus={() => q && setOpen(true)}
          role="combobox"
          aria-label="Drill name"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList ? optionId(current) : undefined}
          autoComplete="off"
          enterKeyHint="done"
          maxLength={120}
          onKeyDown={onKeyDown}
        />
        <label className="flex items-center gap-1 text-[13px] text-muted">
          <input type="number" min={1} max={180} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="field-input tnum w-16 text-right" aria-label="Minutes for a quick drill" />
          min
        </label>
        <Button type="submit" disabled={!q}>
          Add
        </Button>
        <button type="button" className="btn-ghost px-2" onClick={onCancel} aria-label="Stop adding">
          <X className="size-4" />
        </button>
      </div>

      {showList && (
        <ul id={listId} role="listbox" aria-label="Matching drills" className="mt-2 overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
          {matches.map((m, i) => (
            <li
              key={m.key}
              id={optionId(i)}
              role="option"
              aria-selected={i === current}
              // Keep focus in the box so the list doesn't close before the tap lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
              onMouseMove={() => i !== current && setActive(i)}
              className={`flex cursor-pointer items-center gap-3 border-b border-line px-3 py-2 last:border-b-0 ${i === current ? 'bg-action-soft' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] leading-snug font-medium">{m.title}</p>
                <p className="tnum mt-0.5 flex items-center gap-1 text-[12px] text-muted">
                  {m.hasDiagram && <Spline className="size-3 shrink-0" aria-label="Has a diagram" />}
                  {m.minutes} min
                  {!m.byName && <span>· matches its skills or notes</span>}
                </p>
              </div>
              <span className="shrink-0 rounded-full border border-line-strong px-2 py-0.5 text-[11px] text-muted">
                {m.origin === 'shared' && m.from ? `From ${m.from.split(' ')[0]}` : ORIGIN_LABEL[m.origin]}
              </span>
            </li>
          ))}
          <li
            id={optionId(matches.length)}
            role="option"
            aria-selected={current === matches.length}
            onMouseDown={(e) => e.preventDefault()}
            onClick={addQuick}
            onMouseMove={() => current !== matches.length && setActive(matches.length)}
            className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-[14px] ${current === matches.length ? 'bg-action-soft' : ''}`}
          >
            <Plus className="size-4 shrink-0 text-action" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              Add “{q}” as a quick drill <span className="tnum text-muted">({clampMinutes(minutes)} min)</span>
            </span>
          </li>
          {loading && <li className="px-3 pb-2 text-[12px] text-muted">Still loading some drills…</li>}
        </ul>
      )}

      <p className="mt-2 text-[13px] text-muted">
        Or browse your library
        <span className="hidden lg:inline"> on the right</span>
        <span className="lg:hidden">
          :{' '}
          <button type="button" className="font-medium text-action underline-offset-2 hover:underline" onClick={onLibrary}>
            open library
          </button>
        </span>
        .
      </p>
    </form>
  );
}
